import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { Loader2, Users, Activity, FileText, TrendingUp } from "lucide-react";

const ADMIN_EMAIL = "chowdry.bader@gmail.com";

interface WorkspaceStat {
  id: number;
  name: string;
  plan: string;
  createdAt: string;
  lastOrchestrationAt: string | null;
  recentErrorCount: number;
  geminiSpendCents: number;
}

interface AdminOverview {
  totalWorkspaces: number;
  totalLeads: number;
  contentPublishedThisWeek: number;
  workspaces: WorkspaceStat[];
}

interface AiBotLeaderboard {
  sinceDate: string;
  topBots: { bot: string; totalHits: number }[];
  topUrls: { url: string; totalHits: number }[];
  byWorkspace: { workspaceId: number; workspaceName: string; totalHits: number }[];
}

function StatCard({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: number | string }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5 flex items-center gap-4">
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon size={18} className="text-primary" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold text-foreground">{value}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, isLoading: authLoading } = useAuth();

  const { data, isLoading, error } = useQuery<AdminOverview>({
    queryKey: ["/api/admin/overview"],
    enabled: !authLoading && user?.email === ADMIN_EMAIL,
  });

  const { data: leaderboard } = useQuery<AiBotLeaderboard>({
    queryKey: ["/api/admin/ai-bot-leaderboard"],
    enabled: !authLoading && user?.email === ADMIN_EMAIL,
  });

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || user.email !== ADMIN_EMAIL) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-muted-foreground text-sm">
        Access denied. Admin only.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-destructive text-sm">
        Failed to load admin overview.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Cross-workspace overview — DRPR.AI</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Users} label="Total Workspaces" value={data.totalWorkspaces} />
        <StatCard icon={TrendingUp} label="Leads (All Time)" value={data.totalLeads} />
        <StatCard icon={FileText} label="Content Published (7d)" value={data.contentPublishedThisWeek} />
      </div>

      {leaderboard && leaderboard.topBots.length > 0 && (
        <div>
          <h2 className="text-base font-semibold text-foreground mb-1">AI Citation Leaderboard</h2>
          <p className="text-xs text-muted-foreground mb-3">Which AI tools crawl your content — last 30 days (since {leaderboard.sinceDate})</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-xl p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Top AI Bots</p>
              <div className="space-y-2">
                {(() => {
                  const max = Math.max(...leaderboard.topBots.map((b) => b.totalHits), 1);
                  return leaderboard.topBots.slice(0, 10).map((b) => (
                    <div key={b.bot} className="flex items-center gap-3">
                      <span className="text-sm text-foreground w-40 truncate">{b.bot}</span>
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${Math.round((b.totalHits / max) * 100)}%` }} />
                      </div>
                      <span className="text-sm text-muted-foreground w-16 text-right">{b.totalHits.toLocaleString()}</span>
                    </div>
                  ));
                })()}
              </div>
            </div>
            <div className="bg-card border border-border rounded-xl p-5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">By Workspace</p>
              <div className="space-y-2">
                {leaderboard.byWorkspace.slice(0, 10).map((w) => (
                  <div key={w.workspaceId} className="flex items-center justify-between">
                    <span className="text-sm text-foreground truncate">{w.workspaceName}</span>
                    <span className="text-sm text-muted-foreground">{w.totalHits.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div>
        <h2 className="text-base font-semibold text-foreground mb-3">Workspaces</h2>
        <div className="border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                {["ID", "Name", "Plan", "Created", "Last Orch.", "Errors (30d)", "Gemini Spend"].map((h) => (
                  <th key={h} className="text-left px-4 py-2 text-xs text-muted-foreground font-medium uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.workspaces.map((ws, i) => (
                <tr key={ws.id} className={i % 2 === 0 ? "bg-background" : "bg-muted/20"}>
                  <td className="px-4 py-3 text-muted-foreground">{ws.id}</td>
                  <td className="px-4 py-3 font-medium text-foreground">{ws.name}</td>
                  <td className="px-4 py-3 text-muted-foreground capitalize">{ws.plan || "free"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{ws.createdAt ? new Date(ws.createdAt).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{ws.lastOrchestrationAt ? new Date(ws.lastOrchestrationAt).toLocaleString() : "Never"}</td>
                  <td className="px-4 py-3">
                    <span className={ws.recentErrorCount > 0 ? "text-destructive font-semibold" : "text-muted-foreground"}>
                      {ws.recentErrorCount}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">${(ws.geminiSpendCents / 100).toFixed(2)}</td>
                </tr>
              ))}
              {data.workspaces.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">No workspaces found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
